
import React, { useRef, useEffect } from 'react';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface PageContainerProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  isMobileNavHidden?: boolean;
}

export const PageContainer: React.FC<PageContainerProps> = ({ 
  children, 
  title, 
  subtitle,
  isMobileNavHidden = false
}) => {
  const location = useLocation();
  const showHeader = !['/', '/auth/callback'].includes(location.pathname);
  const { user } = useAuth();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (mainRef.current) {
      console.log(
        "[PageContainer.tsx] main element fontFamily:",
        getComputedStyle(mainRef.current).fontFamily
      );
    }
  }, []);

  // Generate unique IDs for skip links
  const mainContentId = 'main-content';
  const navigationId = 'primary-navigation';

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-x-hidden">
      {/* Skip to main content link for keyboard users */}
      <a
        href={`#${mainContentId}`}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
        onFocus={(e) => {
          // Ensure the link is visible when focused
          e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      >
        Skip to main content
      </a>

      {/* Skip to navigation link for keyboard users */}
      {!isMobileNavHidden && (
        <a
          href={`#${navigationId}`}
          className="sr-only focus:not-sr-only focus:absolute focus:top-16 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
          onFocus={(e) => {
            e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
        >
          Skip to navigation
        </a>
      )}

      {showHeader && (
        <header role="banner">
          <Header title={title} subtitle={subtitle} />
        </header>
      )}
      
      <main
        id={mainContentId}
        ref={mainRef}
        role="main"
        aria-label={title ? `${title} page content` : 'Main page content'}
        tabIndex={-1}
        className={cn(
          "flex-1 animate-fade-in px-4 pt-1 pb-16 md:px-4 md:pt-10 md:pb-8 focus:outline-none",
          user && "md:ml-[var(--sidebar-width)] transition-[margin-left] duration-300"
        )}
        data-testid="main-content"
      >
        <div
          className="mx-auto w-full max-w-full md:max-w-7xl"
          data-testid="page-container"
        >
          {children}
        </div>
      </main>
      
      <nav
        id={navigationId}
        role="navigation"
        aria-label="Primary navigation"
        className="z-[200]"
      >
        <Navigation isHidden={isMobileNavHidden} />
      </nav>
    </div>
  );
};
